import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { filter } from 'rxjs/operators';
import './ValidationError.css';
import './ValidationErrorTheme.css';

import {
  inputValidatedSubject,
  runValidationSubject,
  validateInputSubject,
  validator,
} from './validator';

interface ValidationProps {
  inputId: string;
  formId: string;
  validationFieldId?: string;
  active?: boolean;
  forceCircle?: boolean;
  validateFunc?: () => undefined | string[] | Promise<undefined | string[]>;
  dirty?: boolean;
  standalone?: boolean;
  value: any;
  validatableRef?: React.RefObject<HTMLElement>;
}

function getHTMLElement(input: unknown): HTMLElement | null {
  if (input instanceof HTMLElement) {
    return input;
  }

  if (typeof input === 'object' && input !== null && 'element' in input) {
    const el = (input as { element: unknown }).element;
    if (el instanceof HTMLElement) {
      return el;
    }
  }

  return null;
}

export const ValidationError: FC<ValidationProps> = ({
  inputId,
  formId,
  validationFieldId,
  standalone,
  active: outsideActive,
  value,
  validateFunc,
  dirty: outsideDirty,
  forceCircle,
  validatableRef: inputRef,
}) => {
  const [error, setError] = useState<string | null>();
  const [isValid, setValid] = useState(false);
  const [isDirty, setDirty] = useState(outsideDirty || false);
  const [isValidating, setValidating] = useState(false);
  const lastValue = useRef(value);
  const errorTextRef = useRef<HTMLSpanElement>(null);

  const ids = useRef({ formId, inputId });

  const validateSelf = useCallback(
    async (showErrors: boolean) => {
      if (!validateFunc) {
        return;
      }
      setValidating(true);
      const newError = await validateFunc();
      setValidating(false);
      if (showErrors) {
        setTimeout(() => {
          setDirty(true);
        }, 100);
      }
      if (newError) {
        setError(newError[0]);
        setValid(false);
      } else {
        setError(null);
        setValid(true);
      }
      inputValidatedSubject.next({
        error: newError ? true : false,
        formId,
        inputId,
      });
      return newError;
    },
    [validateFunc, formId, inputId]
  );

  const validateSelfRef = useRef(validateSelf);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (lastValue.current !== value) {
        lastValue.current = value;
        if (!isDirty) {
          setDirty(true);
        }
      }
    }, 0);
    return () => {
      clearTimeout(timeout);
    };
  }, [value]);

  useEffect(() => {
    if (outsideActive) {
      setDirty(true);
    }
  }, [outsideActive]);

  useEffect(() => {
    if (outsideDirty) {
      setDirty(true);
    }
  }, [outsideDirty]);

  useEffect(() => {
    validateSelfRef.current = validateSelf;
  }, [validateSelf]);

  useEffect(() => {
    const sub1 = runValidationSubject
      .pipe(filter(({ validatedFormId }) => validatedFormId === formId))
      .subscribe(async ({ showErrors, validatedFormId }) => {
        if (validateFunc) {
          validateSelfRef.current(showErrors);
        }
      });
    const sub2 = validateInputSubject
      .pipe(
        filter(
          ({ validatedFormId, validatedInputId }) =>
            validatedFormId === formId && validatedInputId === inputId
        )
      )
      .subscribe(async ({ showErrors }: { showErrors: boolean }) => {
        if (validateFunc) {
          validateSelfRef.current(showErrors);
        }
      });

    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
    };
  }, [formId, inputId]);

  useEffect(() => {
    const savedFormId = ids.current.formId;
    const savedInputId = ids.current.inputId;
    validator.register(savedFormId, savedInputId);
    return () => {
      validator.unregister(savedFormId, savedInputId);
    };
  }, []);

  useEffect(() => {
    if (!validationFieldId) return;

    const timer = setTimeout(() => {
      const validationField = document.getElementById(validationFieldId);
      if (validationField) {
        validationField.addEventListener('focusout', () => {
          validateSelfRef.current(true);
        });
      }
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const input = getHTMLElement(inputRef?.current);
    if (!input) {
      return;
    }

    if (errorTextRef.current) {
      input.setAttribute('aria-describedby', inputId + '-error-text');
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('aria-describedby');
      input.removeAttribute('aria-invalid');
    }
  }, [error, isDirty]);

  if (!validateFunc) {
    return null;
  }

  return (
    <>
      {!isDirty && !standalone ? null : (
        <div
          className={
            'validation-error ' +
            (isValidating ? 'validation-error--active ' : '') +
            (isValid ? 'validation-error--valid ' : '') +
            (standalone ? 'validation-error--standalone ' : '') +
            (isDirty ? 'validation-error--dirty ' : '') +
            (isValid && (!value || (typeof value === 'string' && !value.length))
              ? 'validation-error--optional-valid '
              : '')
          }
        >
          <div className="validation-error__border"></div>
          {error && isDirty && !outsideActive ? (
            <span className="validation-error__text" role='alert' id={inputId + '-error-text'} ref={errorTextRef}>
              {error}
            </span>
          ) : null}
        </div>
      )}
    </>
  );
};
